import { cantidadAutomatica } from './buildTestBody';
import type { Celda, CeldaEstado, PoseCelda, TestSet } from './tipos';

/**
 * Máquina de estados de las celdas de una corrida (spec 2026-08-23):
 *   pendiente → disparada → completa | fallida | sin_resultado
 * Completitud por CONTEO de filas por execution_id (regla del proyecto: nunca
 * "estabilidad corta"); sin fila tras el timeout → sin_resultado (el 4xx temprano
 * no deja fila). Todo puro: la persistencia vive en setsApi, el disparo en ejecutar.
 */

const FINALES: ReadonlySet<CeldaEstado> = new Set(['completa', 'fallida', 'sin_resultado']);
export const TIMEOUT_SIN_RESULTADO_MS = 10 * 60_000;

/**
 * Expansión por pose: una config con N poses seleccionadas produce N celdas por
 * producto, cada una con un config_id DERIVADO estable (`base::p:<poseId>` /
 * `base::c:<customId>`). La derivación es determinística, así dos corridas del
 * mismo set alinean celda a celda — y las evaluaciones (keyed por config_id)
 * distinguen poses sin cambiar el schema.
 */
function clavesDePose(config: TestSet['configs'][number]): string[] {
  // El modo automático no tiene poses (el director de arte no las mira). Una config
  // que ANTES fue personalizada arrastra `poses` en su payload: sin este corte se
  // expandiría en celdas fantasma que mandan todas el mismo body.
  if (config.payload.modo_test === 'automatico') return [''];
  const claves = [
    ...(config.payload.poses ?? []).map((id) => `p:${id}`),
    ...(config.payload.poses_custom ?? []).map((c) => `c:${c.id}`),
  ];
  return claves.length > 0 ? claves : [''];
}

/** Filas que tiene que entregar una celda de esta config (automático: 1..6). */
function esperadasDe(config: TestSet['configs'][number]): number {
  return config.payload.modo_test === 'automatico' ? cantidadAutomatica(config.payload) : 1;
}

export function configBaseId(configIdDerivado: string): string {
  const i = configIdDerivado.indexOf('::');
  return i === -1 ? configIdDerivado : configIdDerivado.slice(0, i);
}

/** Resuelve la pose que le toca a una celda expandida (null = default del backend). */
export function poseDeCelda(
  set: Pick<TestSet, 'configs'>,
  configIdDerivado: string,
): PoseCelda {
  const i = configIdDerivado.indexOf('::');
  if (i === -1) return null;
  const clave = configIdDerivado.slice(i + 2);
  const base = set.configs.find((c) => c.config_id === configBaseId(configIdDerivado));
  if (clave.startsWith('p:')) return { tipo: 'catalogo', id: clave.slice(2) };
  if (clave.startsWith('c:')) {
    const id = clave.slice(2);
    const custom = base?.payload.poses_custom?.find((c) => c.id === id);
    return custom ? { tipo: 'custom', id, filename: custom.filename } : null;
  }
  return null;
}

export function crearCeldas(set: Pick<TestSet, 'variante_ids' | 'configs'>): Celda[] {
  return set.configs.flatMap((config) => {
    // Productos propios de la config si los tiene; sino hereda los del set.
    const variantes = config.variante_ids?.length ? config.variante_ids : set.variante_ids;
    const esperadas = esperadasDe(config);
    return clavesDePose(config).flatMap((clave) =>
      variantes.map((varianteId) => ({
        config_id: clave ? `${config.config_id}::${clave}` : config.config_id,
        variante_id: varianteId,
        execution_id: crypto.randomUUID(),
        estado: 'pendiente' as const,
        disparada_en: null,
        esperadas,
      })),
    );
  });
}

/**
 * Completitud por CONTEO de filas por execution_id (regla del proyecto: las filas de
 * una tanda llegan con gaps de 30-60s, así que "apareció una" no significa "terminó").
 * Una celda del modo automático pide N imágenes en una sola llamada y comparte
 * execution_id entre las N.
 *
 * Cualquier fila con error hunde la celda entera: la cadena forzada del harness no
 * tiene fallback, así que un error es EL resultado de esa configuración.
 *
 * Si vence el timeout con filas parciales la celda se cierra como `completa`, no como
 * `sin_resultado`: las imágenes que llegaron existen, se pagaron y sirven para votar.
 */
export function aplicarPoll(
  celdas: Celda[],
  filas: Array<{ execution_id: string; has_error: boolean | null }>,
  ahoraMs: number,
  timeoutMs: number = TIMEOUT_SIN_RESULTADO_MS,
): Celda[] {
  const conteo = new Map<string, { ok: number; error: boolean }>();
  for (const f of filas) {
    const acc = conteo.get(f.execution_id) ?? { ok: 0, error: false };
    if (f.has_error) acc.error = true;
    else acc.ok += 1;
    conteo.set(f.execution_id, acc);
  }
  return celdas.map((c) => {
    if (FINALES.has(c.estado) || c.estado === 'pendiente') return c;
    const acc = conteo.get(c.execution_id);
    if (acc?.error) return { ...c, estado: 'fallida' };
    if (acc && acc.ok >= (c.esperadas ?? 1)) return { ...c, estado: 'completa' };
    if (c.disparada_en != null && ahoraMs - c.disparada_en > timeoutMs) {
      return { ...c, estado: acc && acc.ok > 0 ? 'completa' : 'sin_resultado' };
    }
    return c;
  });
}

export function celdasPendientes(celdas: Celda[]): Celda[] {
  return celdas.filter((c) => c.estado === 'pendiente');
}

export function corridaTerminada(celdas: Celda[]): boolean {
  return celdas.length > 0 && celdas.every((c) => FINALES.has(c.estado));
}

/** Reintento manual: execution_id NUEVO (el conteo nunca mezcla intentos). */
export function reintentarCelda(celdas: Celda[], configId: string, varianteId: string): Celda[] {
  return celdas.map((c) =>
    c.config_id === configId && c.variante_id === varianteId
      ? { ...c, execution_id: crypto.randomUUID(), estado: 'pendiente', disparada_en: null }
      : c,
  );
}

/**
 * Imágenes que va a generar (y cobrar) una corrida. NO es `celdas.length`: una celda
 * del modo automático pide hasta 6 en una sola llamada, así que contar celdas
 * subestimaría el gasto justo en el aviso de créditos.
 */
export function imagenesDeCorrida(celdas: Celda[]): number {
  return celdas.reduce((n, c) => n + (c.esperadas ?? 1), 0);
}
